import React, { Component } from "react";
import eventService from "../../services/eventService";
import slideShowService from "../../services/slideshowService";
import storageService from "../../services/storageService";
import BrochureAddToPdfButtonComponent from "../addToPdfButtonComponent/addToPdfButton.component";
import BrochureYourPdfButtonComponent from "../yourPdfButtonComponent/yourPdfButton.component";
import BrochureTrayComponent from "../trayComponent/tray.component";
import { BrochureCartState, EDirection } from "../enums";
import { IBrochurePDFConfig } from "../types/pdfConfig";
import { IBrochurePDFCartApplicationState } from "../types/states";
class BrochurePDFCartApplication extends Component<any, IBrochurePDFCartApplicationState> {
  constructor(props: null) {
    super(props);
    this.state = {
      cartState: BrochureCartState.EMPTY,
      enabled: false,
      pdfConfigs: storageService.brochurePDFConfigs,
      pdfConfigsCategories: storageService.brochurePDFConfigsCategories,
      trayOpen: false,
    };
  }

  componentDidMount() {
    eventService.on("pdf-brochure-app:disable", this.disable);
    eventService.on("pdf-brochure-app:enable", this.enable);
    eventService.on("pdf-brochure-app:clear", this.clearCart);
    eventService.on("pdf-brochure-app:sync", this.syncToCart);
    if (this.state.pdfConfigs.length > 0) {
      this.setState({
        cartState: BrochureCartState.LISTING,
      });
    }
  }

  componentWillUnmount() {
    eventService.off("pdf-brochure-app:disable", this.disable);
    eventService.off("pdf-brochure-app:enable", this.enable);
    eventService.off("pdf-brochure-app:clear", this.clearCart);
    eventService.off("pdf-brochure-app:sync", this.syncToCart);
  }

  render() {
    if (!this.state.enabled) return null;
    return (
      <div className="pdf-app">
        <BrochureYourPdfButtonComponent
          count={this.state.pdfConfigs.length}
          openTray={this.openTray}
          trayOpen={this.state.trayOpen}
        />
        <BrochureTrayComponent
          sections={this.state.pdfConfigs}
          indexedCategories={this.state.pdfConfigsCategories}
          closeTray={this.closeTray}
          continue={this.continue}
          cartState={this.state.cartState}
          open={this.state.trayOpen}
          removeSlide={this.removePdf}
        />
        <BrochureAddToPdfButtonComponent
          onClick={this.addPdf}
          requireDownload={false}
          openTray={this.openTray}
        />
      </div>
    )
  }

  // Calls out to the HomeController to generate an image of the current Slide. On success
  // add the image to local storage and update the state.
  addPdf = () => {
    if (slideShowService.currentChapterController.getPDFConfig) {
      this.setCartState(BrochureCartState.LOADING);
      this.openTray();
      slideShowService.currentChapterController
        .getPDFConfig()
        .then((pdfConfig: IBrochurePDFConfig[]) => {
          this.storePdf(pdfConfig);
          this.setCartState(BrochureCartState.LISTING);
          slideShowService.currentChapterController.clear();
        });
    }
  };

  openTray = () => {
    this.setState({
      trayOpen: true,
    });
  };

  closeTray = () => {
    this.setState({
      trayOpen: false,
    });
  };

  setCartState = (cartState: BrochureCartState) => {
    this.setState({
      cartState,
    });
  };

  continue = () => {
    // We need to close the tray and then alert the chapter controller that we need to move onto the
    // next step. We also want to make sure we sync the accounts over.
    this.syncToController();
    slideShowService.currentChapterController.nextSection();
  };

  storePdf(config: IBrochurePDFConfig[]) {
    const pdfConfigs = [...this.state.pdfConfigs, ...config];
    // Derive the categories from the new items and add new categories to the
    // end of the list.
    const pdfConfigsCategories = this.deriveCategories(config);
    this.setState({
      pdfConfigs,
      pdfConfigsCategories,
    });
    storageService.brochurePDFConfigs = pdfConfigs;
    storageService.brochurePDFConfigsCategories = pdfConfigsCategories;
    this.syncToController();
    slideShowService.currentChapterController.setCartEmpty(pdfConfigs.length === 0);
  }

  removePdf = (config: IBrochurePDFConfig) => {
    const pdfConfigs = this.state.pdfConfigs.filter((c) => c !== config);
    const pdfConfigsCategories = this.deriveCategories(pdfConfigs);
    this.setState({
      pdfConfigs,
      pdfConfigsCategories,
    });
    storageService.brochurePDFConfigs = pdfConfigs;
    storageService.brochurePDFConfigsCategories = pdfConfigsCategories;
    // Set if cart is empty on the ChapterController.
    slideShowService.currentChapterController.setCartEmpty(pdfConfigs.length === 0);
    this.syncToController();
  };

  deriveCategories(config: IBrochurePDFConfig[]): string[] {
    // Derive the categories from the list of items and add new categories to the
    // end of the list.
    const categories: string[] = config.map((c) => c.meta.category);
    const pdfConfigsCategories: string[] = [
      ...new Set([...this.state.pdfConfigsCategories, ...categories]),
    ];

    return pdfConfigsCategories;
  };

  setAccounts = (e: Event, selectedAccounts: IBrochurePDFConfig[]) => {
    this.setState({
      pdfConfigs: selectedAccounts,
    });
    storageService.brochurePDFConfigs = selectedAccounts;
  };

  get disabled(): boolean {
    return !this.state.enabled;
  }

  disable = () => {
    this.setState({
      enabled: false,
    });
  };

  enable = () => {
    this.setState({
      enabled: true,
    });
  };

  clearCart = () => {
    this.setState({
      pdfConfigs: [],
      pdfConfigsCategories: [],
    });
    storageService.brochurePDFConfigs = [];
    storageService.brochurePDFConfigsCategories = [];
  };

  syncToCart = async () => {
    // Get PDF information from the current chapter controller and set them on our state.
    const pdfConfigs = await slideShowService.currentChapterController.getCartPDFConfigs();
    const pdfConfigsCategories = this.deriveCategories(pdfConfigs);
    this.setState({
      pdfConfigs,
      pdfConfigsCategories,
    });
    storageService.brochurePDFConfigs = pdfConfigs;
    storageService.brochurePDFConfigsCategories = pdfConfigsCategories;
  }

  syncToController = () => {
    // We need to take our current selection and grab the chapter controller and call the
    // setCartAccounts function with our current configs.
    // This ensures that we keep the selection process of Section 5 separate from what's actually
    // in the cart.
    slideShowService.currentChapterController.setCartAccounts(this.state.pdfConfigs);
  };
}

export default BrochurePDFCartApplication;
